import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  PackageSearch, PackageOpen, Warehouse as WarehouseIcon, Layers,
  Truck, Send, PackageCheck, Users, AlertTriangle, Boxes,
} from "lucide-react";

const CARDS = [
  { key: "incoming", label: "Waiting Confirmation", icon: PackageSearch, color: "text-amber-300", to: "/warehouse/incoming" },
  { key: "received_today", label: "Received Today", icon: PackageOpen, color: "text-purple-300", to: "/warehouse/received" },
  { key: "in_warehouse_total", label: "Total in Warehouse", icon: WarehouseIcon, color: "text-indigo-300", to: "/warehouse/sorting" },
  { key: "sorting", label: "Ready for Sorting", icon: Layers, color: "text-indigo-300", to: "/warehouse/sorting" },
  { key: "in_batch", label: "In Outbound Batch", icon: Boxes, color: "text-sky-300", to: "/warehouse/batches" },
  { key: "active_batches", label: "Active Batches", icon: Truck, color: "text-teal-300", to: "/warehouse/batches" },
  { key: "shipped_today", label: "Shipped Today", icon: Send, color: "text-orange-300", to: "/warehouse/shipped" },
  { key: "ready_last_mile", label: "Ready for Last-Mile", icon: PackageCheck, color: "text-emerald-300", to: "/warehouse/ready-last-mile" },
  { key: "dispatchers_active", label: "Active Dispatchers", icon: Users, color: "text-cyan-300", to: "/warehouse/dispatchers" },
  { key: "dispatchers_inactive", label: "Inactive Dispatchers", icon: Users, color: "text-slate-600 dark:text-slate-400", to: "/warehouse/dispatchers" },
  { key: "issues_open", label: "Open Issues", icon: AlertTriangle, color: "text-red-300", to: "/warehouse/issues" },
];

export default function WarehouseOverview() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/warehouse/stats")
      .then(r => setStats(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="wh-overview-page" className="space-y-6">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Warehouse</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{stats.warehouse_name || "Warehouse"} Dashboard</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Live snapshot of intake, sorting, and outbound activity.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {CARDS.map(c => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`wh-stat-${c.key}`}
            className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/50 dark:bg-slate-900/50 hover:bg-slate-100 hover:dark:bg-slate-900 hover:border-slate-300 hover:dark:border-slate-700 transition-colors group"
          >
            <div className="flex items-start justify-between">
              <div data-testid={`wh-stat-${c.key}-label`} className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">{c.label}</div>
              <c.icon className={`w-4 h-4 ${c.color}`} />
            </div>
            <div data-testid={`wh-stat-${c.key}-value`} className="mt-3 font-display text-3xl font-bold tracking-tight">
              {loading ? "—" : (stats[c.key] ?? 0)}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
